// src/logger.ts

// Debug state - stored in localStorage for persistence
let debugEnabled = localStorage.getItem("debug") === "true"

// Check debug mode
const isDebugMode = () => debugEnabled

// Global function to toggle debug mode
const enableDebug = (enabled = true) => {
  debugEnabled = enabled
  localStorage.setItem("debug", String(enabled))
  console.log(`🐛 Debug mode ${enabled ? "ENABLED" : "DISABLED"}`)
  return enabled
}

// Expose debug controls to window/global scope
if (typeof window !== "undefined") {
  const w = window as any
  w.debug = {
    on: () => enableDebug(true),
    off: () => enableDebug(false),
    toggle: () => enableDebug(!debugEnabled),
    status: () => {
      console.log(`Debug is currently ${debugEnabled ? "ON 🟢" : "OFF 🔴"}`)
      return debugEnabled
    },
  }

  // Also expose shorthand
  w.debugOn = () => enableDebug(true)
  w.debugOff = () => enableDebug(false)
}

// Style map per log level
const styles = {
  log: "color: #4caf50; background: #e8f5e9; padding: 2px 4px; border-radius: 2px;",
  info: "color: #2196f3; background: #e3f2fd; padding: 2px 4px; border-radius: 2px;",
  warn: "color: #ff9800; background: #fff3e0; padding: 2px 4px; border-radius: 2px;",
  error: "color: #f44336; background: #ffebee; padding: 2px 4px; border-radius: 2px;",
}

const nsStyle = "color: white; background: #607d8b; padding: 2px 4px; border-radius: 2px;"

export const createLogger = (namespace?: string) => {
  const hasNamespace = Boolean(namespace)
  const nsPrefix = hasNamespace ? `%c[${namespace}]` : ""
  const nsArgs = hasNamespace ? [nsStyle] : []

  const withCaller = (...args: any[]) => {
    const callerInfo = getCallerInfo()
    return [`[${callerInfo}]`, ...args]
  }

  return {
    log: (...args: any[]) => {
      if (isDebugMode()) {
        console.log(`${nsPrefix}%c[LOG]`, ...nsArgs, styles.log, ...withCaller(...args))
      }
    },
    info: (...args: any[]) => {
      if (isDebugMode()) {
        console.info(`${nsPrefix}%c[INFO]`, ...nsArgs, styles.info, ...withCaller(...args))
      }
    },
    warn: (...args: any[]) => {
      if (isDebugMode()) {
        console.warn(`${nsPrefix}%c[WARN]`, ...nsArgs, styles.warn, ...withCaller(...args))
      }
    },
    error: (...args: any[]) => {
      if (isDebugMode()) {
        console.error(`${nsPrefix}%c[ERROR]`, ...nsArgs, styles.error, ...withCaller(...args))
      }
    },
  }
}

function getCallerInfo(): string {
  const error = new Error()
  const stack = error.stack?.split("\n")

  if (!stack || stack.length < 5) {
    return "unknown"
  }

  const callerLine = stack[4] || stack[3]

  const match =
    callerLine.match(/\s*at\s+(.*)\s+\((.*):(\d+):(\d+)\)/) ||
    callerLine.match(/\s*at\s+(.*):(\d+):(\d+)/)

  if (match) {
    const functionName = match[1].replace(/^Object\./, "")
    const filePathRaw = match[2] || match[1]
    const lineNumber = match[3]

    const filePath = filePathRaw
      .replace(/^.*\/src\//, "/src/") // clean absolute path
      .replace(/\?.*$/, "") // 🔥 remove ?t=... query

    return `${functionName} (${filePath}:${lineNumber})`
  }

  return callerLine.trim()
}

// Default global logger without namespace
export const logger = createLogger()
